import { Download } from '@carbon/react/icons';
import { Button } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import {
  ExportSpatialData,
  SpatialExportBanner,
  SPATIAL_EXPORT_MAX_FEATURES,
  type SpatialExportSelection,
  type SpatialLayerOption,
} from 'components/export-spatial-data';
import { datasetExportSnapshotAtom, visibleDatasetIdsAtom } from './atoms';
import { datasetRegistry } from './datasets';
import { useMapQuerySpatialExport, type SpatialExportRequest } from './hooks';

/**
 * The Map Query "Export Spatial Data" entry point (the button on the map toolbar). Builds the layer
 * roster from the dataset registry — every visible dataset that declares a `spatialExport` descriptor
 * (and is not a raster layer) — using each config's declared backend key + attribute catalog and its
 * live count + committed filters, opens the shared ExportSpatialData dialog, and submits one combined
 * export spanning every checked dataset. The roster here is not hardcoded per dataset: a dataset joins
 * it just by declaring a `spatialExport` descriptor on its config. Note the backend is not yet fully
 * descriptor-driven (AC 9) — a new dataset also needs a `GisExport::Sources::MapQuery::DATASET_KEYS`
 * entry plus its own column catalog and layer method before the combined export can actually write it.
 *
 * @return {JSX.Element} The Export button, the dialog, and the async status banner.
 */
export function MapQuerySpatialExport() {
  const [open, setOpen] = useState(false);
  const { exportStatus, startExport, resetExport, isExporting, userId } =
    useMapQuerySpatialExport();

  const visibleIds = useAtomValue(visibleDatasetIdsAtom);
  const snapshot = useAtomValue(datasetExportSnapshotAtom);

  const { layers, payloadByLayer } = useMemo(() => {
    const rosterLayers: SpatialLayerOption[] = [];
    const payloads: Record<string, Record<string, unknown>> = {};
    Object.entries(datasetRegistry).forEach(([feId, config]) => {
      const spatial = config.spatialExport;
      // Only datasets that declare a spatial export, are not raster layers, and are currently visible.
      if (!spatial || config.rasterLocator || !visibleIds.includes(feId))
        return;
      const snap = snapshot[feId];
      rosterLayers.push({
        id: spatial.key,
        label: config.label,
        count: snap?.count,
        attributes: spatial.attributes.map((attribute) => ({ ...attribute })),
      });
      payloads[spatial.key] = snap?.payload ?? {};
    });
    return { layers: rosterLayers, payloadByLayer: payloads };
  }, [visibleIds, snapshot]);

  return (
    <>
      <Button
        kind="tertiary"
        size="md"
        renderIcon={Download}
        onClick={() => setOpen(true)}
        disabled={!userId}
      >
        Export
      </Button>
      <SpatialExportBanner status={exportStatus} onDismiss={resetExport} />
      {open && (
        <ExportSpatialData
          open={open}
          onClose={() => setOpen(false)}
          layers={layers}
          isExporting={isExporting}
          maxFeatures={SPATIAL_EXPORT_MAX_FEATURES}
          onExport={(selection: SpatialExportSelection) => {
            const datasets: SpatialExportRequest['datasets'] = {};
            selection.layers.forEach(({ id, attributeIds }) => {
              datasets[id] = {
                params: payloadByLayer[id] ?? {},
                attributes: attributeIds,
              };
            });
            setOpen(false);
            void startExport({ formats: selection.formats, datasets });
          }}
        />
      )}
    </>
  );
}
